import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { getRelationshipManagementSalesOrderConfiguration } from 'src/services/relationshipmanagement';
import {
  DepotDetailsDto,
  OperationalProfileInformationDto,
  RDCInformationDto,
  SalesOrderReferenceTypeDto
} from 'src/models/salesorder';
import {
  CustomerDto,
  CutOffInformationDto,
  GeneralConfigInformationDto,
  HazardousInformationDto,
  OperationalInformationDto
} from 'src/models/relationshipmanagement';
import { getSalesOrderSalesOrderConfiguration } from '../../../services/salesorder';
import { QueryProps, baseQueryFn } from 'src/queries/baseQueryFunctions';

export type CombinedConfig = {
  customer?: CustomerDto;
  depotDetails?: DepotDetailsDto;
  mandatoryReferences?: SalesOrderReferenceTypeDto[];
  maxSalesOrderNumberLength?: number;
  minSalesOrderNumberLength?: number;
  operationalProfileInformation?: OperationalProfileInformationDto;
  rdcInformation?: RDCInformationDto[];
  cutOffTimes?: CutOffInformationDto;
  generalConfiguration?: GeneralConfigInformationDto;
  hazardous?: HazardousInformationDto;
  operational?: OperationalInformationDto;
};

/**
 * Combined (sales order & relationship management) sales order configuration object.
 */
export const getCombinedSalesOrderConfigData = async (customerId: string) => {
  const [salesOrderResponse, relationshipManagemtResponse] = await Promise.all([
    getSalesOrderSalesOrderConfiguration(customerId),
    getRelationshipManagementSalesOrderConfiguration(customerId)
  ]);

  return {
    data: {
      ...(salesOrderResponse?.data ?? {}),
      ...(relationshipManagemtResponse?.data ?? {}),
      configurationUpdateAvailable: false
    }
  };
};

/**
 * get combined (sales order & relationship management) sales order configuration object.
 */
export const useGetCombinedSalesOrderConfigDataQuery = (
  customerId: string,
  {
    enabled = false,
    initialData = undefined,
    onSuccess = undefined,
    onSettled = undefined,
    refetchOnWindowFocus = false,
    refetchOnMount = true,
    staleTime = 5 * 60 * 1000,
    customResponseModifier = undefined,
    queryKeyOverride
  }: QueryProps<CombinedConfig, {}>
): UseQueryResult<CombinedConfig> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['combinedSalesOrderConfig', customerId],
    queryFn: () =>
      baseQueryFn({
        serviceFn: () => getCombinedSalesOrderConfigData(customerId),
        customResponseModifier
      }),
    enabled: enabled,
    initialData,
    initialDataUpdatedAt: 0, // treat placeholder initialData as stale so the query still fetches on first mount
    refetchOnWindowFocus,
    refetchOnMount,
    staleTime,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) }),
    ...(typeof onSettled === 'function' && { onSettled: () => onSettled() })
  });
};
